<template>
  <div class="roles-page py-3">
    <header class="roles-toolbar">
      <div class="roles-toolbar__title">
        <span class="roles-toolbar__accent" aria-hidden="true"></span>
        <div>
          <h1>Roles</h1>
          <p>Define roles and see how many users are assigned to each</p>
        </div>
      </div>
      <div class="roles-toolbar__actions">
        <span v-if="lastUpdatedAt && !isLoading" class="roles-updated">
          Updated {{ lastUpdatedLabel }}
        </span>
        <CButton
          size="sm"
          class="refresh-btn"
          :class="{ 'is-loading': isLoading }"
          :disabled="isLoading"
          @click="loadData"
        >
          <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="refresh-btn__icon" aria-hidden="true">
            <polyline points="23 4 23 10 17 10"/>
            <polyline points="1 20 1 14 7 14"/>
            <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/>
          </svg>
          <span>{{ isLoading ? 'Refreshing…' : 'Refresh' }}</span>
        </CButton>
        <CButton
          color="primary"
          size="sm"
          class="create-role-btn"
          @click="goToCreate"
        >
          <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" class="create-role-btn__icon" aria-hidden="true">
            <line x1="12" y1="5" x2="12" y2="19"/>
            <line x1="5" y1="12" x2="19" y2="12"/>
          </svg>
          <span>Create Role</span>
        </CButton>
      </div>
    </header>

    <CAlert v-if="errorMessage" color="danger" show class="roles-alert">{{ errorMessage }}</CAlert>

    <div class="summary-tiles">
      <div class="summary-tile summary-tile--total">
        <span class="summary-tile__label">Total Roles</span>
        <span class="summary-tile__value">{{ roles.length }}</span>
      </div>
      <div class="summary-tile summary-tile--inuse">
        <span class="summary-tile__label">Roles In Use</span>
        <span class="summary-tile__value">{{ rolesInUseCount }}</span>
      </div>
      <div class="summary-tile summary-tile--assigned">
        <span class="summary-tile__label">User Assignments</span>
        <span class="summary-tile__value">{{ totalAssignments }}</span>
      </div>
    </div>

    <BaseTable
      :items="filteredRoles"
      :columns="columns"
      title="All Roles"
      accent="linear-gradient(180deg, #0e5b46, #16a07a)"
      :loading="isLoading"
      :default-sort="{ column: 'id', direction: 'asc' }"
      striped
      hover
      bordered
      sticky-header
      class="roles-table"
    >
      <template #toolbar>
        <div class="roles-search-wrap">
          <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="roles-search-icon" aria-hidden="true">
            <circle cx="11" cy="11" r="7"/>
            <line x1="21" y1="21" x2="16.65" y2="16.65"/>
          </svg>
          <input
            v-model.trim="searchTerm"
            type="text"
            class="roles-input"
            placeholder="Search roles…"
          />
          <button
            v-if="searchTerm"
            type="button"
            class="roles-search-clear"
            aria-label="Clear search"
            @click="searchTerm = ''"
          >×</button>
        </div>
      </template>

      <template #cell-name="{ row }">
        <span :class="['role-pill', roleClass(row.name)]">{{ formatRole(row.name) }}</span>
      </template>

      <template #cell-guard_name="{ row }">
        <span class="role-guard">{{ row.guard_name || '—' }}</span>
      </template>

      <template #cell-userCount="{ row }">
        <span class="role-user-count">
          <span class="role-user-count__value">{{ row.userCount }}</span>
          <span class="role-user-count__label">{{ row.userCount === 1 ? 'user' : 'users' }}</span>
        </span>
      </template>

      <template #empty>
        <div v-if="!roles.length" class="roles-empty">
          <div class="roles-empty__title">No roles yet</div>
          <div class="roles-empty__hint">
            <button type="button" class="roles-empty__link" @click="goToCreate">Create your first role</button> to get started.
          </div>
        </div>
        <div v-else class="roles-empty">
          <div class="roles-empty__title">No matches for "{{ searchTerm }}"</div>
          <div class="roles-empty__hint">
            Try a different name, or
            <button type="button" class="roles-empty__link" @click="searchTerm = ''">clear search</button>.
          </div>
        </div>
      </template>
    </BaseTable>
  </div>
</template>

<script>
import axios from 'axios'
import BaseTable from '@/components/BaseTable.vue'

export default {
  name: 'SettingsRoles',
  components: { BaseTable },
  data () {
    return {
      isLoading: false,
      errorMessage: '',
      searchTerm: '',
      roles: [],
      lastUpdatedAt: null,
      columns: [
        { key: 'id', label: 'Id', width: '64px' },
        { key: 'name', label: 'Role' },
        { key: 'userCount', label: 'Users', width: '140px', align: 'right' }
      ]
    }
  },
  computed: {
    filteredRoles () {
      const term = this.searchTerm.toLowerCase()
      if (!term) return this.roles
      return this.roles.filter((role) =>
        [role.id, role.name, role.guard_name]
          .filter(Boolean)
          .some((value) => String(value).toLowerCase().includes(term))
      )
    },
    rolesInUseCount () {
      return this.roles.filter((role) => role.userCount > 0).length
    },
    totalAssignments () {
      return this.roles.reduce((sum, role) => sum + (role.userCount || 0), 0)
    },
    lastUpdatedLabel () {
      if (!this.lastUpdatedAt) return ''
      const hours = String(this.lastUpdatedAt.getHours()).padStart(2, '0')
      const minutes = String(this.lastUpdatedAt.getMinutes()).padStart(2, '0')
      return `${hours}:${minutes}`
    }
  },
  created () {
    this.loadData()
  },
  methods: {
    loadData () {
      this.isLoading = true
      this.errorMessage = ''

      const token = localStorage.getItem('api_token')
      const rolesPromise = axios.get(`${this.$apiAdress}/api/role?token=${token}`)
      const usersPromise = axios.get(`${this.$apiAdress}/api/users?token=${token}`)

      Promise.all([rolesPromise, usersPromise])
        .then(([rolesRes, usersRes]) => {
          const rolesData = this.extractRoles(rolesRes.data)
          const usersData = this.extractUsers(usersRes.data)
          const counts = this.countUsersPerRole(rolesData, usersData)

          this.roles = rolesData.map((role) => ({
            ...role,
            userCount: counts[role.name] || 0
          }))
          this.lastUpdatedAt = new Date()
        })
        .catch((error) => {
          console.log(error)
          this.errorMessage = error?.response?.data?.message || 'Unable to load roles right now.'
        })
        .finally(() => {
          this.isLoading = false
        })
    },
    extractRoles (data) {
      if (Array.isArray(data)) return data
      if (Array.isArray(data?.roles)) return data.roles
      if (Array.isArray(data?.data)) return data.data
      return []
    },
    extractUsers (data) {
      if (Array.isArray(data)) return data
      if (Array.isArray(data?.users)) return data.users
      if (Array.isArray(data?.data)) return data.data
      return []
    },
    countUsersPerRole (roles, users) {
      const counts = {}
      roles.forEach((role) => { counts[role.name] = 0 })

      users.forEach((user) => {
        const userRoles = Array.isArray(user.roles) ? user.roles : []
        userRoles.forEach((role) => {
          const name = typeof role === 'string' ? role : role?.name
          if (name && Object.prototype.hasOwnProperty.call(counts, name)) {
            counts[name] += 1
          }
        })
      })

      return counts
    },
    formatRole (name) {
      if (!name) return '—'
      return String(name).replace(/_/g, ' ')
    },
    roleClass (role) {
      const r = String(role || '').toLowerCase()
      if (!r) return 'role-pill--gray'
      if (r === 'superadmin' || r === 'admin') return 'role-pill--violet'
      if (r === 'telesales' || r === 'crm' || r === 'agent') return 'role-pill--blue'
      if (r === 'packer' || r === 'storekeeper' || r === 'field_staff') return 'role-pill--amber'
      if (r === 'accountant' || r === 'junior_accountant') return 'role-pill--teal'
      if (r === 'moderator' || r === 'sales_supervisor' || r === 'sales_admin' || r === 'ecom_manager' || r === 'marketing') return 'role-pill--green'
      return 'role-pill--gray'
    },
    goToCreate () {
      this.$router.push('/settings/roles/create')
    }
  }
}
</script>

<style scoped>
.roles-page {
  padding: 0.25rem 0;
}

/* ── Toolbar ── */
.roles-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 0.75rem;
  padding: 0.25rem 0.25rem 1rem;
}

.roles-toolbar__title {
  display: flex;
  align-items: center;
  gap: 0.65rem;
}

.roles-toolbar__accent {
  width: 4px;
  height: 36px;
  border-radius: 4px;
  background: linear-gradient(180deg, #0e5b46 0%, #16a07a 100%);
  flex-shrink: 0;
}

.roles-toolbar__title h1 {
  margin: 0;
  font-size: 1.15rem;
  font-weight: 700;
  color: #1f2a44;
  letter-spacing: -0.01em;
  line-height: 1.2;
}

.roles-toolbar__title p {
  margin: 0;
  font-size: 0.78rem;
  color: #6b7280;
}

.roles-toolbar__actions {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.65rem;
}

.roles-updated {
  font-size: 0.75rem;
  color: #6b7280;
  font-weight: 500;
}

.roles-toolbar__actions >>> .btn.create-role-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  height: 34px;
  padding: 0 1rem;
  border-radius: 8px;
  font-weight: 600;
  font-size: 0.82rem;
  background: var(--brand-gradient, linear-gradient(135deg, #0e5b46 0%, #16a07a 100%));
  border: 0;
  color: #ffffff;
  box-shadow: 0 2px 6px rgba(14, 91, 70, 0.18);
  transition: transform 0.15s ease, box-shadow 0.15s ease, filter 0.15s ease;
}

.roles-toolbar__actions >>> .btn.create-role-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 6px 14px rgba(14, 91, 70, 0.28);
  filter: brightness(1.05);
  color: #ffffff;
}

.roles-toolbar__actions >>> .btn.create-role-btn:active {
  transform: translateY(0);
  filter: brightness(0.96);
}

.create-role-btn__icon {
  flex-shrink: 0;
  display: block;
}

.roles-toolbar__actions >>> .btn.refresh-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  height: 34px;
  padding: 0 0.85rem;
  border-radius: 8px;
  font-weight: 600;
  font-size: 0.82rem;
  background: #ffffff;
  border: 1px solid #e5e9f2;
  color: #1f2a44;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
  transition: border-color 0.15s ease, background 0.15s ease, color 0.15s ease, box-shadow 0.15s ease;
}

.roles-toolbar__actions >>> .btn.refresh-btn:hover:not(:disabled) {
  border-color: #16a07a;
  color: #0e5b46;
  background: #f5fbf8;
  box-shadow: 0 2px 6px rgba(14, 91, 70, 0.12);
}

.roles-toolbar__actions >>> .btn.refresh-btn:disabled {
  opacity: 1;
  background: #f8fafc;
  color: #4b5563;
  cursor: progress;
}

.refresh-btn__icon {
  flex-shrink: 0;
  display: block;
  color: #16a07a;
  transition: transform 0.4s ease;
}

.roles-toolbar__actions >>> .btn.refresh-btn:hover:not(:disabled) .refresh-btn__icon {
  transform: rotate(60deg);
}

.roles-toolbar__actions >>> .btn.refresh-btn.is-loading .refresh-btn__icon {
  animation: refresh-spin 0.9s linear infinite;
  color: #16a07a;
}

@keyframes refresh-spin {
  to { transform: rotate(360deg); }
}

/* ── Alert ── */
.roles-alert {
  margin: 0 0 0.75rem;
  border-radius: 10px;
}

/* ── Summary tiles ── */
.summary-tiles {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0.75rem;
  margin-bottom: 1rem;
}

.summary-tile {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  padding: 0.85rem 1rem;
  border-radius: 12px;
  background: #ffffff;
  border: 1px solid #e5e9f2;
  box-shadow: 0 2px 6px rgba(15, 23, 42, 0.04);
  border-left-width: 4px;
}

.summary-tile--total    { border-left-color: #1f2a44; }
.summary-tile--inuse    { border-left-color: #16a07a; }
.summary-tile--assigned { border-left-color: #6d28d9; }

.summary-tile__label {
  font-size: 0.68rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #4b5563;
}

.summary-tile__value {
  font-size: 1.25rem;
  font-weight: 700;
  color: #1f2a44;
}

/* ── Table card ── */
.roles-table {
  margin-bottom: 1rem;
}

/* ── Search (rendered into BaseTable's toolbar slot) ── */
.roles-search-wrap {
  position: relative;
  width: 280px;
  max-width: 100%;
}

.roles-search-icon {
  position: absolute;
  top: 50%;
  left: 0.75rem;
  transform: translateY(-50%);
  color: #6b7280;
  pointer-events: none;
}

.roles-input {
  width: 100%;
  height: 34px;
  padding: 0.3rem 2.25rem 0.3rem 2.1rem;
  border: 1px solid #d8dde6;
  border-radius: 999px;
  background: #ffffff;
  font-size: 0.82rem;
  color: #2f3640;
  font-family: inherit;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

.roles-input:focus {
  outline: none;
  border-color: #16a07a;
  box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.18);
}

.roles-search-clear {
  position: absolute;
  top: 50%;
  right: 0.5rem;
  transform: translateY(-50%);
  width: 22px;
  height: 22px;
  border: 0;
  border-radius: 50%;
  background: #e5e9f2;
  color: #4b5563;
  font-size: 1rem;
  line-height: 1;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: background 0.15s ease, color 0.15s ease;
}

.roles-search-clear:hover {
  background: #d1d5db;
  color: #1f2a44;
}

/* ── Empty state inside BaseTable ── */
.roles-empty {
  text-align: center;
  padding: 1.5rem 1rem;
}

.roles-empty__title {
  font-size: 0.95rem;
  font-weight: 700;
  color: #1f2a44;
  margin-bottom: 0.25rem;
}

.roles-empty__hint {
  font-size: 0.82rem;
  color: #6b7280;
}

.roles-empty__link {
  border: 0;
  background: transparent;
  padding: 0;
  color: #0e5b46;
  font-weight: 600;
  cursor: pointer;
}

.roles-empty__link:hover {
  text-decoration: underline;
}

/* ── Cell content (role pill, guard chip, user count) ── */
.role-pill {
  display: inline-flex;
  align-items: center;
  padding: 0.18rem 0.6rem;
  border-radius: 6px;
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.02em;
  text-transform: capitalize;
  white-space: nowrap;
}

.role-pill--gray   { background: #f1f5f9; color: #475569; }
.role-pill--violet { background: #ede9fe; color: #5b21b6; }
.role-pill--blue   { background: #dbeafe; color: #1e40af; }
.role-pill--amber  { background: #fef3c7; color: #92400e; }
.role-pill--teal   { background: #ccfbf1; color: #115e59; }
.role-pill--green  { background: #dff5ea; color: #0e5b46; }

.role-guard {
  font-size: 0.72rem;
  font-weight: 600;
  color: #6b7280;
  background: #f1f5f9;
  padding: 0.18rem 0.55rem;
  border-radius: 6px;
  display: inline-block;
}

.role-user-count {
  display: inline-flex;
  align-items: baseline;
  gap: 0.35rem;
  justify-content: flex-end;
}

.role-user-count__value {
  font-size: 1rem;
  font-weight: 700;
  color: #1f2a44;
  letter-spacing: -0.01em;
}

.role-user-count__label {
  font-size: 0.72rem;
  color: #6b7280;
  font-weight: 500;
}

/* ── Responsive ── */
@media (max-width: 991px) {
  .summary-tiles {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 767px) {
  .roles-toolbar {
    align-items: flex-start;
  }

  .roles-toolbar__title {
    flex: 1 1 100%;
  }

  .roles-toolbar__actions {
    flex: 1 1 100%;
    justify-content: flex-start;
  }

  .summary-tiles {
    grid-template-columns: 1fr 1fr;
  }

  .roles-search-wrap {
    width: 100%;
  }
}

@media (max-width: 575px) {
  .roles-page {
    padding: 0 0 0.5rem;
  }
  .roles-toolbar {
    padding: 0.25rem 0 0.75rem;
    gap: 0.5rem;
  }
  .roles-toolbar__accent {
    height: 30px;
  }
  .roles-toolbar__title h1 {
    font-size: 1rem;
  }
  .roles-toolbar__title p {
    font-size: 0.72rem;
  }
  .roles-toolbar__actions {
    gap: 0.45rem;
  }
  .roles-toolbar__actions >>> .btn.create-role-btn,
  .roles-toolbar__actions >>> .btn.refresh-btn {
    height: 32px;
    font-size: 0.76rem;
    padding: 0 0.7rem;
  }
  .summary-tiles {
    grid-template-columns: 1fr;
  }
  .summary-tile {
    padding: 0.65rem 0.8rem;
  }
  .summary-tile__value {
    font-size: 1.1rem;
  }
}
</style>
